import BelgiumIcon from 'assets/svg/icons/BelgiumIcon';
import dayjs from 'dayjs';
import { get, isNumber, last } from 'lodash';
import type { ITax, IThresholdTax } from 'types/taxes.types';
import { TaxTypeEnum } from 'types/taxes.types';
import { VATTypeEnum, type IUser } from 'types/users.types';
import isTaxDone from 'utils/taxes/isTaxDone';
import SpecialVATStatementTableData from './SpecialVATStatementTableData.json';
import VATStatementTableData from './VATStatementTableData.json';
import { CURRENT_YEAR } from 'utils/constants';

const today = dayjs();
const currentYear = today.year();
const taxes = {
  firstClosingDate: dayjs(`01/21/${currentYear}`),
  canSkipPrepayment: true,
  incomeTaxSlug: 'be/income-tax-submission',
  shouldShowCompanyField: () => false,
  getTaxAvailablityMessage: () =>
    'taxes.api.not_available_yet_from_authorities',
  incomeTaxPaymentSlug: 'be/income-tax-payment',
  getStatementTableData: (tax: ITax) => {
    switch (tax.type) {
      case TaxTypeEnum.vatSubmission: {
        if (tax.isSpecial) {
          return SpecialVATStatementTableData;
        }
        return VATStatementTableData;
      }
    }
  },
  vat_breakdown_get_path: 'be/vat',
  required_payment_summary_field: 'vat_number',
  local_flag_for_vat_franchisee: (tax: IThresholdTax) => BelgiumIcon,
  eur_breakdown_get_path: '',
  startDateForIncomeTaxSubmission: dayjs(`01-05-${currentYear}`),
  getVATFileAvailabilityMessage: (tax: ITax) => {
    if (last(tax?.submissions)?.submissionRef) {
      return 'taxes.vat.minfin.file_availability_message';
    }
    return '';
  },
  download_vat_submission_xml: true,
  currentThresholdLabel: 'country.be',
  required_data_for_intraeu_export: ['VATNumber', 'Address', 'PhoneNumber'],
  required_data_for_vat_submission: ['VATNumber', 'Address'],
  hasCopyrightHelper: true,
  incomeTaxPaymentAmount: (tax: ITax): number | undefined => {
    if (isTaxDone(tax)) {
      return tax?.paidAmount || 0;
    }
    if (tax?.period?.year === CURRENT_YEAR) {
      return (
        (get(tax, 'projectedIncomeTax') || 0) -
        (get(tax, 'incomeTaxPrepaymentTotal') || 0)
      );
    }
    return (
      (!get(tax, 'employeeSalary') ? get(tax, 'estimatedOtherTaxes') || 0 : 0) +
      (get(tax, 'estimatedIncomeTax') || 0) -
      (get(tax, 'incomeTaxPrepaymentTotal') || 0)
    );
  },
  submission_description_suffix:
    'taxes.submission_history.alert.description.be',
  VATLateWarning: 'taxes.tax_submission.notDone.title.past.VAT',
  VATLatePaymentWanrning: 'taxes.vat_payment.passed_due_date.warning.VAT',
  submission_history_message: 'taxes.submission_history.XML_exported',
  beforeDueDatePaymentWarning: 'taxes.vat_payment.before_due_date.warning2.VAT',
  beforeDueDateWarning:
    'taxes.tax_submission.notDone.title_with_early_warning.VAT',
  getIsReimbursement: (tax: ITax): boolean => {
    if (tax?.type === TaxTypeEnum.vatPayment) {
      return (
        isNumber(get(tax, 'statement.c72')) && get(tax, 'statement.c72') > 0
      );
    }
    if (tax?.type === TaxTypeEnum.incomeTaxPayment)
      return get(tax, 'suggestedPrepaymentAmount') < 0;
    return false;
  },
  shouldShowCompanyTaxRate: true,
  blocked: {
    web: false,
    experts: false,
  },
  fullTermsLink: 'https://finances.belgium.be/fr/entreprises/tva/amendes',
  can_make_vat_as_expense: false,
  vat: {
    frequency: ['quarterly'], // Could have more than one value, possibly 'yearly' and/or 'monthly' depending on the country
    submission_dates: {
      quarterly: [
        {
          day: 20,
          month: 4,
        },
        {
          day: 10,
          month: 8,
        },
        {
          day: 20,
          month: 10,
        },
        {
          day: 20,
          month: 1,
        },
      ],
    },
    due_dates: {
      quarterly: [
        {
          day: 20,
          month: 4,
        },
        {
          day: 20,
          month: 7,
        },
        {
          day: 20,
          month: 10,
        },
        {
          day: 20,
          month: 1,
        },
      ],
    },
  },
  social_contributions: {
    frequency: ['quarterly'],
    due_dates: {
      quarterly: [
        {
          day: 25,
          month: 3,
        },
        {
          day: 25,
          month: 6,
        },
        {
          day: 25,
          month: 9,
        },
        {
          day: 25,
          month: 12,
        },
      ],
    },
  },
  vat_return_frequency: ['quarterly'],
  default_vat_return_frequency: 'quarterly',
  hasProfitAndLossBreakdown: true,
  no_negative_vat_cells: ['c71', 'c72'],
  available_export_modules: [
    { key: 'excel', value: 'excel' },
    { key: 'original_documents', value: 'original_documents' },
    { key: 'profit_and_loss', value: 'profit_and_loss' },
    { key: 'ubl', value: 'ubl' },
    { key: 'pl', value: 'pl' },
    { key: 'coda', value: 'coda' },
    { key: 'pdf', value: 'pdf' },
    { key: 'amortisation_table', value: 'amortisation_table' },
  ],
  available_full_period_export_modules: [],
  taxes_onboarding_modal_texts: {
    canUse: [
      'taxes.onboarding_modal.can_use_text_1.be',
      'taxes.onboarding_modal.can_use_text_2.be',
      'taxes.onboarding_modal.can_use_text_3.be',
      'taxes.onboarding_modal.can_use_text_4.be',
      'taxes.onboarding_modal.can_use_text_5.be',
    ],
    cannotUse: [
      'taxes.onboarding_modal.cannot_use_text_1.be',
      'taxes.onboarding_modal.cannot_use_text_2.be',
      'taxes.onboarding_modal.cannot_use_text_3.be',
      'taxes.onboarding_modal.cannot_use_text_4.be',
      'taxes.onboarding_modal.cannot_use_text_5.be',
      'taxes.onboarding_modal.cannot_use_text_6.be',
      'taxes.onboarding_modal.cannot_use_text_7.be',
      'taxes.onboarding_modal.cannot_use_text_8.be',
    ],
  },
  onboardingTaxObligations: (user: IUser) => {
    const mapper = {
      [VATTypeEnum.subjectToVAT]: [
        {
          title: 'taxes_onboarding.obligations.vat',
          frequency: 'quarterly',
          helper: 'taxes_onboarding.obligations.vat.helper.be',
        },
        {
          title: 'taxes_onboarding.obligations.income_tax',
          frequency: 'yearly',
          helper: 'taxes_onboarding.obligations.income_tax.helper.be',
        },
        {
          title: 'taxes_onboarding.obligations.client_listing',
          frequency: 'yearly',
          helper: 'taxes_onboarding.obligations.client_listing.helper.be',
        },
        {
          title: 'taxes_onboarding.obligations.social_contributions',
          frequency: 'quarterly',
          helper: 'taxes_onboarding.obligations.social_contributions.helper.be',
        },
      ],
      default: [
        {
          title: 'taxes_onboarding.obligations.income_tax',
          frequency: 'yearly',
          helper: 'taxes_onboarding.obligations.income_tax.helper.be',
        },
        {
          title: 'taxes_onboarding.obligations.client_listing',
          frequency: 'yearly',
          helper: 'taxes_onboarding.obligations.client_listing.helper.be',
        },
        {
          title: 'taxes_onboarding.obligations.social_contributions',
          frequency: 'quarterly',
          helper: 'taxes_onboarding.obligations.social_contributions.helper.be',
        },
      ],
    };

    return mapper[user?.VATType as keyof typeof mapper] || mapper.default;
  },
} as const;

export default taxes;
